import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { CatalogSource } from '@1po/1po-bff-fe-spec/generated/common/vehicle/CatalogSource';
import { VehicleDetail } from '@1po/1po-bff-fe-spec/generated/common/vehicle/VehicleDetail';
import { InputRef } from 'antd';
import { ROUTER_CATALOG_VEHICLE, ROUTER_HOME } from 'app/AppRouter';
import { RootState } from 'app/AppStore';
import { trackAppEvent } from 'app/AppTracker';
import { GlobeIcon, MicrophoneIcon, SearchIcon, TimesCircleIcon } from 'assets/icons';
import { FirstHelpPopin } from 'components/Help/FirstHelpPopin';
import { fetchVehicleByVINVRN } from 'domains/catalog/Catalog.requests';
import { getLastVehicle, getSearchVehicleResult, wasQueryRequested } from 'domains/catalog/Catalog.store';
import { UNIVERSAL_PRODUCTS } from 'domains/catalog/Catalog.types';
import { getCatalogSourceUrl } from 'domains/catalog/Catalog.utils';
import { DEFAULT_COUNTRY, getUserCountry } from 'domains/user';
import {
  DarkMagnifierButton,
  FlexMenuContainer,
  MagnifierButton,
  SContainer,
  SContainerError,
  SCountryHeader,
  SCountryItemName,
  SCountryMenuItem,
  SCountryMenuList,
} from 'pages/HomePage/search/SearchByVINVRN.styled';

import { Box, CenterFlex, Dropdown as AntDropdown, Flex, Icon, Input, MarginBox, Pipeline, Spin, Text } from 'UI';
import {
  ERROR,
  focusRef,
  FOUND,
  hasData,
  NOT_FOUND,
  RefType,
  SEARCH_STATUS,
  useFocus,
  useFocusOnMount,
  useLarge,
} from 'utils';
import { TRACKING_EVENT_VIN_VRN_SEARCH } from 'utils/eventTracker/EventTracker.types';
import { Country } from 'utils/i18n/Country';
import { SearchQueryType, SearchVariantOption, SearchVariantType, SelectSearchVariant } from './SearchBar';

const SearchByVINVRNError = ({
  search,
  setSearch,
  setIsError,
  userCountry,
  searchInputRef,
  searchStatus,
}: {
  search: SearchQueryType;
  setSearch: (search: SearchQueryType) => void;
  setIsError: (error: boolean) => void;
  userCountry: string | undefined;
  searchInputRef: RefType<InputRef>;
  searchStatus: SEARCH_STATUS;
}) => {
  const large = useLarge();
  const { t } = useTranslation();

  return large ? (
    <SContainerError align={'center'} data-cy={'vehicle-not-found-error'}>
      <Box align={'center'} width={60}>
        <CenterFlex>
          <Icon IconComponent={TimesCircleIcon} width={14} height={14} color={'red'} display={'inline'} noPointer />
        </CenterFlex>
      </Box>
      <Flex justify={'center'} align={'center'}>
        <Text type="base" color="grey35" ellipsis>
          {searchStatus === NOT_FOUND
            ? t(
                'catalog.parts.search.by_vin_or_vrn.not_found',
                'Sorry, we could not find any vehicle associated, please verify your input.',
              )
            : t(
                'catalog.parts.search.by_vin_or_vrn.error',
                'Sorry, we could not find any vehicle associated, please try again later.',
              )}
        </Text>
        <MarginBox mr={15} />
      </Flex>
      <Flex justify={'flex-end'} align={'center'} maxWidth={130}>
        <Text
          type="base"
          color="clear_blue"
          data-cy={'try-again-button'}
          onClick={() => {
            setSearch({
              ...search,
              pendingQuery: undefined,
              country: Country.findByKey(userCountry) ?? DEFAULT_COUNTRY,
            });
            setIsError(false);
            focusRef(searchInputRef);
          }}
          cursor={'pointer'}
          hoverUnderLine
        >
          {t('common.try_again', 'Try again')}
        </Text>
        <MarginBox mr={15} />
      </Flex>
    </SContainerError>
  ) : (
    <Box>
      <SContainerError small align={'center'}>
        <MarginBox ml={10} />
        <MarginBox mt={4}>
          <Icon IconComponent={TimesCircleIcon} width={14} height={14} color={'red'} display={'inline'} />
        </MarginBox>
        <MarginBox ml={8} />
        <Text type="sm" color="red">
          {t('catalog.parts.search.by_vin_or_vrn.not_found_mobile', 'Sorry, we could not find any vehicle.')}
        </Text>
        <MarginBox mr={10} />
      </SContainerError>
      <Flex direction={'row'} wrap={'nowrap'}>
        <MarginBox ml={32} />
        <Text type="sm" color="red">
          {t('catalog.parts.search.by_vin_or_vrn.verify_your_input_and', 'Please verify your input and')}
        </Text>
        <MarginBox ml={5} />
        <Text
          type="sm"
          color="clear_blue"
          onClick={() => {
            setSearch({
              query: '',
              pendingQuery: undefined,
              country: Country.findByKey(userCountry) ?? DEFAULT_COUNTRY,
            });
            setIsError(false);
          }}
          cursor={'pointer'}
          hoverUnderLine
        >
          {t('common.try_again', 'Try again')}
        </Text>
        <Text type="sm" color="clear_blue">{`.`}</Text>
      </Flex>
    </Box>
  );
};

export const SearchByVinVrnInputId = 'search-vin_vrn-input';
export const SearchByVinVrnInputName = 'search-vin_vrn-input-name';

const countryOptions = Country.SearchCountryList;

type LastVehicleLike = {
  vehicleDetail?: VehicleDetail;
};

function renderSearchActionButton({
  darkSearchButton,
  onSearch,
}: Readonly<{
  darkSearchButton?: boolean;
  onSearch: () => void;
}>): React.ReactElement {
  if (darkSearchButton) {
    return (
      <DarkMagnifierButton onClick={onSearch} data-cy={'search-button'}>
        <Icon IconComponent={SearchIcon} size={27} color={'white'} />
      </DarkMagnifierButton>
    );
  }

  return (
    <MagnifierButton onClick={onSearch} data-cy={'search-button'}>
      <MarginBox mt={6} ml={-3}>
        <Icon IconComponent={SearchIcon} size={27} color={'black'} />
      </MarginBox>
    </MagnifierButton>
  );
}

function applyPendingSearchResult({
  pendingQuery,
  searchStatus,
  searchCatalogSource,
  onFound,
  lastVehicle,
  history,
  params,
  search,
  setSearch,
  setIsError,
}: {
  pendingQuery?: string;
  searchStatus?: SEARCH_STATUS;
  searchCatalogSource?: CatalogSource | typeof UNIVERSAL_PRODUCTS;
  onFound?: (vehicleDetail: VehicleDetail) => void;
  lastVehicle?: LastVehicleLike;
  history: ReturnType<typeof useHistory>;
  params: URLSearchParams;
  search: SearchQueryType;
  setSearch: (search: SearchQueryType) => void;
  setIsError: (error: boolean) => void;
}): void {
  if (!pendingQuery) {
    return;
  }

  if (searchStatus === FOUND && hasData(searchCatalogSource)) {
    if (onFound && lastVehicle?.vehicleDetail !== undefined) {
      onFound(lastVehicle.vehicleDetail);
    } else {
      history.push(
        `${getCatalogSourceUrl(searchCatalogSource)}/${
          lastVehicle?.vehicleDetail?.vehicleKey
        }${ROUTER_CATALOG_VEHICLE}?${params}`,
      );
    }
    setSearch({ ...search, pendingQuery: undefined });
    return;
  }

  if (searchStatus === NOT_FOUND || searchStatus === ERROR) {
    setIsError(true);
    setSearch({ ...search, pendingQuery: undefined });
  }
}

// NOSONAR - Component coordinates multiple UI states and side effects by design.
export function SearchByVINVRN({
  searchVariant,
  setSearchVariant,
  searchVariantOptions,
  visible,
  onFound,
  hideVariantSelector,
  darkSearchButton,
  showMicrophoneIcon,
  inputPlaceholder,
}: Readonly<{
  searchVariant: SearchVariantType;
  setSearchVariant: (x: SearchVariantType) => void;
  searchVariantOptions: SearchVariantOption[];
  visible?: boolean;
  onFound?: (vehicleDetail: VehicleDetail) => void;
  hideVariantSelector?: boolean;
  darkSearchButton?: boolean;
  showMicrophoneIcon?: boolean;
  inputPlaceholder?: string;
}>) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const history = useHistory();
  const userCountry = useSelector(getUserCountry);
  //eslint-disable-next-line
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const [search, setSearch] = useState<SearchQueryType>({
    query: '',
    pendingQuery: undefined,
    country: Country.findByKey(userCountry) ?? DEFAULT_COUNTRY,
  });
  const [countryMenuOpen, setCountryMenuOpen] = useState(false);
  const lastVehicle = useSelector(getLastVehicle);
  const searchVehicleResult = useSelector((state: RootState) => getSearchVehicleResult(state, search.pendingQuery));
  const isQueryRequested = useSelector((state: RootState) => wasQueryRequested(state, search.pendingQuery));
  const received = lastVehicle?.vehicleDetail?.vehicleIdentifiedBy === search.pendingQuery;
  const searchCatalogSource = received ? lastVehicle?.vehicleDetail?.catalogSource : undefined;
  const searchStatus = received ? lastVehicle?.searchVehicleStatus : searchVehicleResult?.searchVehicleStatus;
  const [isError, setIsError] = useState(false);

  const searchVinVrnRef = useRef<InputRef>(null);
  useFocusOnMount<InputRef>(searchVinVrnRef);
  useFocus<InputRef>(searchVinVrnRef, [!!visible]);

  function onSearch() {
    trackAppEvent(TRACKING_EVENT_VIN_VRN_SEARCH);

    fetchVehicleByVINVRN(
      dispatch,
      search.query,
      search.country.key,
      true,
      undefined, // search in all sources
      undefined,
      (vin) => setSearch({ ...search, pendingQuery: vin }),
      () => setSearch({ ...search, pendingQuery: undefined }),
    );
  }
  const handleCountryItemClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const key = e.currentTarget.dataset.countryKey;
      setSearch({ ...search, country: Country.findByKey(key) ?? DEFAULT_COUNTRY });
      setCountryMenuOpen(false);
    },
    [search],
  );

  useEffect(() => {
    applyPendingSearchResult({
      pendingQuery: search.pendingQuery,
      searchStatus,
      searchCatalogSource,
      onFound,
      lastVehicle,
      history,
      params,
      search,
      setSearch,
      setIsError,
    });
  }, [
    search.pendingQuery,
    history,
    searchStatus,
    search,
    setSearch,
    params,
    searchCatalogSource,
    lastVehicle,
    lastVehicle?.vehicleDetail?.vehicleKey,
    onFound,
    lastVehicle?.vehicleDetail,
  ]);

  const CountryMenu = () => {
    return (
      <FlexMenuContainer direction={'column'}>
        <SCountryHeader>{t('catalog.search.countries', 'Countries')}</SCountryHeader>
        <SCountryMenuList>
          {countryOptions.map((country) => (
            <SCountryMenuItem
              key={country.key}
              data-country-key={country.key}
              $selected={search.country.key === country.key}
              onClick={handleCountryItemClick}
            >
              <Icon IconComponent={country.icon} width={24} height={24} color={'black'} round />
              <SCountryItemName>{country.name}</SCountryItemName>
            </SCountryMenuItem>
          ))}
        </SCountryMenuList>
      </FlexMenuContainer>
    );
  };

  const searchButton = renderSearchActionButton({ darkSearchButton, onSearch });

  return (
    <FirstHelpPopin streamId={ROUTER_HOME} popinId={`${ROUTER_HOME}_veh_search_field`} placement={'bottom'}>
      {isError ? (
        <SearchByVINVRNError
          search={search}
          setSearch={setSearch}
          setIsError={setIsError}
          userCountry={userCountry}
          searchInputRef={searchVinVrnRef}
          searchStatus={searchStatus}
        />
      ) : (
        <SContainer>
          <Flex>
            <Box width={60}>
              <AntDropdown
                dropdownRender={() => <CountryMenu />}
                trigger={['click']}
                open={countryMenuOpen}
                onOpenChange={(isOpen) => setCountryMenuOpen(isOpen)}
              >
                <a className="ant-dropdown-link">
                  <MarginBox ml={19} mt={11}>
                    <Icon IconComponent={search.country?.icon ?? GlobeIcon} size={27} color={'black'} round />
                  </MarginBox>
                </a>
              </AntDropdown>
            </Box>
            <Pipeline size={48} />
            {!hideVariantSelector && (
              <>
                <SelectSearchVariant
                  searchVariant={searchVariant}
                  setSearchVariant={setSearchVariant}
                  searchVariantOptions={searchVariantOptions}
                />
                <Pipeline size={50} offsetRatio={8} />
              </>
            )}
            <Flex size={'1 11'}>
              <Input
                id={SearchByVinVrnInputId}
                name={SearchByVinVrnInputName}
                autoComplete={'section-vin-vrn-search'}
                onChange={(value) => setSearch({ ...search, query: value, pendingQuery: undefined })}
                initialValue={search.query}
                placeholder={
                  inputPlaceholder ??
                  t('catalog.parts.search.by_vin_or_vrn.placeholder', 'Type your VIN or VRN to identify your vehicle')
                }
                onPressEnter={onSearch}
                passRef={searchVinVrnRef}
                data-cy={'vin-search'}
              />
            </Flex>
            {showMicrophoneIcon && (
              <Box width={40} height={50}>
                <MarginBox mt={14} ml={8}>
                  <Icon IconComponent={MicrophoneIcon} size={22} color={'grey55'} />
                </MarginBox>
              </Box>
            )}
            <Box width={60} height={50}>
              {isQueryRequested ? (
                <MarginBox my={13} ml={28}>
                  <Spin size={'small'} />
                </MarginBox>
              ) : (
                searchButton
              )}
            </Box>
          </Flex>
        </SContainer>
      )}
    </FirstHelpPopin>
  );
}
